/*
 * Created on Wed Oct 11 2023 4:56:55 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023 
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Calendar, Loader2, Search, Users, Plus, Minus, Building2, Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import CustomCalendar from '../components/CustomCalendar';
import { getMaxCapacity } from '../../../../../api/action/products';
import { getAllowedPeriodInDays, getMaxAllowedBookingDate, isClosedDay } from '../utils/bookingWindow';
import { isNativeMobileApp } from '../../../../../utils/googleAuth.js';
import { isKioskSessionActive } from '../../../../../domains/flexOffice/kiosk/kioskSession.js';

const BookingForm = ({
  onSearch,
  onBack,
  selectedCategoryId,
  selectedCategory,
  isLoading,
  initialPeople = '',
  initialDate = null,
  initialStartTime = '09:00',
  initialDuration = 'HOUR',
  categories = [],
  onCategoryChange,
  isModifySearch = false,
  searchCriteria = null
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    people: searchCriteria?.people || initialPeople || '1',
    date: searchCriteria?.date || initialDate,
    startTime: searchCriteria?.startTime || initialStartTime,
    duration: 'HOUR'
  });

  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedCategoryIdState, setSelectedCategoryIdState] = useState(selectedCategoryId);
  const [validationErrors, setValidationErrors] = useState({});
  const [maxCapacity, setMaxCapacity] = useState(null);
  const [loadingCapacity, setLoadingCapacity] = useState(false);

  const calendarButtonRef = useRef(null);
  const calendarRef = useRef(null);

  const company = useSelector((state) => state.companyReducer.company);
  const companyTimezone = company?.timezone || company?.operatingLocation?.zoneId || 'America/New_York';
  const companyPrimaryId = useSelector((state) => state.companyReducer.company.primaryId) || '';
  const allowedPeriodInDays = getAllowedPeriodInDays(company);
  const maxAllowedBookingDate = getMaxAllowedBookingDate(company, companyTimezone);

  // Fetch max capacity when category changes
  useEffect(() => {
    const fetchMaxCapacity = async () => {
      if (selectedCategoryIdState && companyPrimaryId) {
        setLoadingCapacity(true);
        try {
          const capacity = await dispatch(getMaxCapacity(selectedCategoryIdState, companyPrimaryId));
          setMaxCapacity(capacity);

          // Reset people value if it exceeds new max capacity
          setFormData(prev => (
            capacity && parseInt(prev.people, 10) > capacity
              ? { ...prev, people: capacity.toString() }
              : prev
          ));
        } catch {
          setMaxCapacity(null);
        } finally {
          setLoadingCapacity(false);
        }
      } else {
        setMaxCapacity(null);
      }
    };

    fetchMaxCapacity();
  }, [selectedCategoryIdState, companyPrimaryId, dispatch]);

  // Reset form when not in modify mode
  useEffect(() => {
    if (!isModifySearch) {
      setSelectedCategoryIdState(selectedCategoryId);
      setValidationErrors({});
    }
  }, [isModifySearch, selectedCategoryId]);

  useEffect(() => {
    if (!formData.date) {
      return;
    }

    const selectedMoment = moment.tz(formData.date, 'YYYY-MM-DD', companyTimezone).startOf('day');
    if (selectedMoment.isAfter(maxAllowedBookingDate, 'day')) {
      setFormData(prev => ({ ...prev, date: null }));
      setValidationErrors(prev => ({
        ...prev,
        date: `Bookings can only be made up to ${allowedPeriodInDays} days in advance`
      }));
    } else if (isClosedDay(selectedMoment, company, companyTimezone)) {
      setFormData(prev => ({ ...prev, date: null }));
      setValidationErrors(prev => ({
        ...prev,
        date: 'Selected date is closed. Please choose another date.'
      }));
    }
  }, [formData.date, companyTimezone, maxAllowedBookingDate, allowedPeriodInDays, company]);

  const handleDateSelect = (dateString) => {
    setFormData(prev => ({ ...prev, date: dateString }));
    setShowCalendar(false);
    if (validationErrors.date) {
      setValidationErrors(prev => ({ ...prev, date: null }));
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) {
      return 'Select Date';
    }
    try {
      return moment.tz(dateString, 'YYYY-MM-DD', companyTimezone).format('M/D/YYYY');
    } catch (error) {
      return dateString;
    }
  };

  const handlePeopleChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (maxCapacity && numValue > maxCapacity) {
        setValidationErrors(prev => ({
          ...prev,
          people: `Cannot exceed ${maxCapacity}`
        }));
        setFormData(prev => ({ ...prev, people: value }));
      } else {
        setFormData(prev => ({ ...prev, people: value }));
        if (validationErrors.people) {
          setValidationErrors(prev => ({ ...prev, people: null }));
        }
      }
    }
  };

  const incrementPeople = () => {
    const current = parseInt(formData.people || '1');
    if (maxCapacity && current >= maxCapacity) {
      setValidationErrors(prev => ({
        ...prev,
        people: `Maximum is ${maxCapacity}`
      }));
      const newValue = (current + 1).toString();
      setFormData(prev => ({ ...prev, people: newValue }));
      return;
    }
    const newValue = (current + 1).toString();
    setFormData(prev => ({ ...prev, people: newValue }));
    if (validationErrors.people) {
      setValidationErrors(prev => ({ ...prev, people: null }));
    }
  };

  const decrementPeople = () => {
    const current = parseInt(formData.people || '1');
    if (current > 1) {
      const newValue = (current - 1).toString();
      setFormData(prev => ({ ...prev, people: newValue }));
      if (validationErrors.people) {
        setValidationErrors(prev => ({ ...prev, people: null }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (isModifySearch && !selectedCategoryIdState) {
      errors.category = 'Please select a space type';
    }

    if (!formData.date) {
      errors.date = 'Please select a date';
    } else {
      const selectedMoment = moment.tz(formData.date, 'YYYY-MM-DD', companyTimezone).startOf('day');
      if (selectedMoment.isAfter(maxAllowedBookingDate, 'day')) {
        errors.date = `Bookings can only be made up to ${allowedPeriodInDays} days in advance`;
      } else if (isClosedDay(selectedMoment, company, companyTimezone)) {
        errors.date = 'Selected date is closed. Please choose another date.';
      }
    }

    if (!formData.people || formData.people === '') {
      errors.people = 'Please enter team size';
    } else {
      const peopleCount = parseInt(formData.people);
      if (isNaN(peopleCount) || peopleCount < 1) {
        errors.people = 'Please enter a valid number';
      } else if (maxCapacity && peopleCount > maxCapacity) {
        errors.people = `Cannot exceed ${maxCapacity}`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstErrorField = Object.keys(validationErrors)[0];
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    const bookingData = {
      ...formData,
      date: formData.date,
      companyTimezone: companyTimezone,
      duration: 'HOUR',
      ...(isModifySearch && { categoryId: selectedCategoryIdState })
    };

    onSearch(bookingData);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCalendar]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showCalendar) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCalendar]);

  useEffect(() => {
    setSelectedCategoryIdState(selectedCategoryId);
  }, [selectedCategoryId]);

  const gridCols = categories.length > 0 ? "md:grid-cols-3" : "md:grid-cols-2";
  // Bottom-sheet feel for the date picker in the native app — never during an
  // active kiosk session, which must render exactly as it always has.
  const dockCalendarToBottom = isNativeMobileApp() && !isKioskSessionActive();

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="bg-white shadow-xl border border-slate-200 max-w-4xl mx-auto fx-m-full-bleed"
        style={{ borderRadius: '16px' }}
      >
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6 text-white rounded-t-2xl fx-m-page">
          <div className="flex items-center justify-between mb-3">
            <div>
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-2xl font-bold"
              >
                Find your workspace
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="text-sm text-slate-300 mt-1 font-bold"
              >
                {isModifySearch
                  ? 'Modify your search criteria'
                  : selectedCategory?.name || 'Select your preferences'}
              </motion.p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.15)' }}
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="flex items-center text-sm text-slate-300 hover:text-white font-semibold bg-white/10 px-4 py-2 backdrop-blur-sm rounded-lg transition-all"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back
            </motion.button>
          </div>
        </div>

        <div className="p-8 space-y-6 fx-m-page">
          <div className={`grid grid-cols-1 ${gridCols} gap-5`}>
            {/* Category Selector */}
            {categories.length > 0 && (
              <motion.div
                id="category"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="md:col-span-1"
              >
                <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
                  <Building2 className="w-4 h-4 mr-1.5 text-blue-600" />
                  Space Type <span className="text-red-500 ml-1">*</span>
                </label>
                <motion.select
                  whileFocus={{ scale: 1.02 }}
                  className={`w-full px-4 py-3 border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium rounded-lg transition-all duration-200 hover:border-slate-300 cursor-pointer ${validationErrors.category
                    ? 'border-red-300 bg-red-50 text-red-900'
                    : 'border-slate-200 text-slate-700'
                    }`}
                  value={selectedCategoryIdState || ''}
                  onChange={(e) => {
                    setSelectedCategoryIdState(e.target.value);
                    if (onCategoryChange) {
                      onCategoryChange(e.target.value);
                    }
                    if (validationErrors.category) {
                      setValidationErrors(prev => ({ ...prev, category: null }));
                    }
                  }}
                >
                  <option value="">Select Space Type</option>
                  {categories.map(cat => (
                    <option key={cat.primaryId} value={cat.primaryId}>
                      {cat.name}
                    </option>
                  ))}
                </motion.select>
                {validationErrors.category && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs text-red-500 mt-1"
                  >
                    {validationErrors.category}
                  </motion.p>
                )}
              </motion.div>
            )}

            {/* Team Size - Number Input with Plus/Minus */}
            <motion.div
              id="people"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="md:col-span-1"
            >
              <label className="flex items-center justify-between text-sm font-semibold text-slate-700 mb-2">
                <div className="flex items-center">
                  <Users className="w-4 h-4 mr-1.5 text-blue-600" />
                  Team Size <span className="text-red-500 ml-1">*</span>
                </div>
                {selectedCategoryIdState && !loadingCapacity && maxCapacity && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-md">
                    <Info className="w-3 h-3 text-blue-500" />
                    <span className="text-xs font-medium text-blue-600">Up to {maxCapacity}</span>
                  </div>
                )}
              </label>

              <div className="flex items-center border-2 border-slate-200 rounded-lg overflow-hidden w-full focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-all">
                <button
                  type="button"
                  onClick={decrementPeople}
                  className="px-4 py-3 bg-white hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  disabled={parseInt(formData.people || '1') <= 1}
                >
                  <Minus className="w-4 h-4 text-slate-600" />
                </button>
                <div className="h-8 w-px bg-slate-200"></div>
                <input
                  type="text"
                  value={formData.people}
                  onChange={handlePeopleChange}
                  className={`w-full px-4 py-3 text-center text-sm font-medium focus:outline-none ${validationErrors.people ? 'text-red-600' : 'text-slate-700'
                    }`}
                  placeholder="1"
                />
                <div className="h-8 w-px bg-slate-200"></div>
                <button
                  type="button"
                  onClick={incrementPeople}
                  className="px-4 py-3 bg-white hover:bg-slate-50 transition-colors"
                >
                  <Plus className="w-4 h-4 text-slate-600" />
                </button>
              </div>

              {validationErrors.people && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-500 mt-1"
                >
                  {validationErrors.people}
                </motion.p>
              )}
            </motion.div>


            {/* Date - Always show */}
            <motion.div
              id="date"
              className="relative md:col-span-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <label className="flex items-center text-sm font-semibold text-slate-700 mb-2">
                <Calendar className="w-4 h-4 mr-1.5 text-blue-600" />
                Date <span className="text-red-500 ml-1">*</span>
              </label>
              <motion.button
                ref={calendarButtonRef}
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowCalendar(!showCalendar)}
                className={`w-full px-4 py-3 border-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium rounded-lg transition-all duration-200 text-left flex items-center justify-between cursor-pointer ${validationErrors.date
                  ? 'border-red-300 bg-red-50'
                  : !formData.date
                    ? 'border-slate-300 text-slate-500 hover:border-slate-400'
                    : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
              >
                <span className={`font-semibold ${validationErrors.date
                  ? 'text-red-900'
                  : !formData.date
                    ? 'text-slate-500'
                    : 'text-slate-700'
                  }`}>
                  {formatDisplayDate(formData.date)}
                </span>
                <Calendar className={`w-4 h-4 ${validationErrors.date
                  ? 'text-red-400'
                  : !formData.date
                    ? 'text-slate-400'
                    : 'text-slate-600'
                  }`} />
              </motion.button>
              {validationErrors.date && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-red-500 mt-1"
                >
                  {validationErrors.date}
                </motion.p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Bookings can be made up to {allowedPeriodInDays} days in advance.
              </p>
            </motion.div>
          </div>

          {/* Hidden duration indicator */}
          <div className="text-xs text-slate-500 text-center">
            Booking duration: Hour (default)
          </div>

          <div className="flex justify-center">
            <motion.button
              type="button"
              onClick={handleSubmit}
              whileHover={!isLoading ? { scale: 1.02, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
              disabled={isLoading || loadingCapacity}
              className={`max-w-xs w-full text-white py-4 text-base font-bold shadow-lg rounded-xl transition-all duration-300 flex items-center justify-center relative overflow-hidden group ${(isLoading || loadingCapacity)
                ? 'bg-gradient-to-r from-slate-400 to-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-xl'
                }`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {(isLoading || loadingCapacity) ? (
                <motion.div className="flex items-center">
                  <Loader2 className="animate-spin w-5 h-5 mr-2" />
                  {loadingCapacity ? 'Loading capacity...' : 'Searching...'}
                </motion.div>
              ) : (
                <motion.div className="flex items-center">
                  <Search className="mr-2 w-5 h-5" />
                  {isModifySearch ? 'Update Search' : 'Search Spaces'}
                </motion.div>
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* CustomCalendar (shared with kiosk's KioskHome.jsx — never edited here)
          renders itself as position:fixed with no inset values, so its
          resolved position depends on browser "static position" fallback
          behavior, which isn't reliably centered/bottom-docked across
          browsers/WebViews. Rather than depend on that, force its actual
          position deterministically via a CSS override targeting its root
          through this stable child-combinator selector — explicit insets
          always win over static-position guessing, regardless of source
          order (the native/bottom-docked rule below is written with higher
          specificity so it wins over this default whenever both apply). */}
      <style>{`
        .fx-calendar-anchor > div {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          right: auto !important;
          bottom: auto !important;
          transform: translate(-50%, -50%) !important;
        }
      `}</style>

      <AnimatePresence>
        {showCalendar && (
          <div
            className="fixed inset-0 z-50"
            style={{ pointerEvents: 'none' }}
          >
            <div
              ref={calendarRef}
              className={`fx-calendar-anchor${dockCalendarToBottom ? ' fx-m-calendar-sheet' : ''}`}
              style={{ pointerEvents: 'auto' }}
            >
              <CustomCalendar
                selectedDate={formData.date}
                onDateSelect={handleDateSelect}
                onClose={() => setShowCalendar(false)}
                allowedPeriodInDays={allowedPeriodInDays}
              />
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingForm;
